import { apiErrorSchema, apiSuccessSchema } from "@yahshi/contracts";
import type { z } from "zod";
import { M1ApiError } from "../modules/m1-app/data/M1AppApi";

export type AdminHttpOptions = {
  baseUrl: string;
  getAccessToken: () => Promise<string>;
  onUnauthorized?: () => void | Promise<void>;
  fetcher?: typeof fetch;
};

/** Authenticated admin transport; callers supply their module's response schema. */
export function createAdminRequest(options: AdminHttpOptions) {
  return async function request<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
    let token: string;
    try {
      token = await options.getAccessToken();
    } catch (error) {
      if (error instanceof M1ApiError && error.code === "UNAUTHORIZED") await options.onUnauthorized?.();
      throw error;
    }
    const response = await (options.fetcher ?? fetch)(`${options.baseUrl.replace(/\/+$/, "")}${path}`, {
      ...init,
      headers: {
        ...init?.headers,
        authorization: `Bearer ${token}`,
        ...(init?.body ? { "content-type": "application/json" } : {}),
      },
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      if (response.status === 401 || response.status === 403) await options.onUnauthorized?.();
      const parsed = apiErrorSchema.safeParse(payload);
      throw parsed.success
        ? new M1ApiError(parsed.data.error.code, parsed.data.error.message, parsed.data.error.fields)
        : new M1ApiError("HTTP_ERROR", `请求失败（${response.status}），请重试`);
    }
    const parsed = apiSuccessSchema(schema).safeParse(payload);
    if (!parsed.success) throw new M1ApiError("INVALID_RESPONSE", "服务响应格式无效，请刷新重试");
    return parsed.data.data;
  };
}
